import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { NavLink as RouterLink } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Add,
  Assignment,
  Edit,
  LocalHospital,
  PeopleOutline,
  Refresh,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import { store } from "../../app/store";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import Paging from "../../components/Layout/Paging";
import Search from "../../components/search/Search";
import CustomDialog from "../../components/modals/CustomDialog";
import Toast from "../../components/Layout/Toast";
import { StyledTableCell, StyledTableRow } from "../../styles/tableStyles";
import { isPrivilegedTransaction } from "../../utils/authUtils";
import { selectAuth } from "../auth/authSlice";
import { RoleTypes } from "../auth/types/roleTypes";
import { selectPreference } from "../preferences/preferencesSlice";
import { EmployeeStatusChip } from "../process/components/EmployeeStatusChip";
import { MedicalStatusChip } from "../process/components/MedicalStatusChip";
import { MedicalDialog } from "../process/components/MedicalDialog";
import { VisaEntry } from "../process/VisaEntry";
import { CallingForm } from "./CallingForm";
import { useOfficePartnerContext } from "./hooks/useOfficePartnerContext";
import {
  employeeOfficeForDisplay,
  employeeOwnerForDisplay,
  officeDisplayLabel,
  officePartnerSourceFromContext,
} from "../process/utils/officePartners";
import { useEmployeeListFetch } from "../process/hooks/useEmployeeListFetch";
import {
  getEmployee,
  removeEmployeeFromListById,
  resetError,
  resetSuccess,
  selectProcesses,
  setSelectedEmployee,
} from "../process/processSlices";
import {
  Employee,
  EmployeeArgs,
  EmployeeStatus,
  MedicalStatus,
} from "../process/types/processTypes";

export const Calling = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");
  const { user } = useAppSelector(selectAuth);
  const {
    context: officeContext,
    loading: officeContextLoading,
    error: officeContextError,
    refresh: refreshOfficeContext,
  } = useOfficePartnerContext();
  const partnerSource = useMemo(
    () => officePartnerSourceFromContext(officeContext, user ?? undefined),
    [officeContext, user],
  );
  const {
    employeesWithCount: { employees, totalCount },
    listLoading,
  } = useAppSelector(selectProcesses);
  const { searchText, agencyId } = useAppSelector(selectPreference);

  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [debouncedSearchText, setDebouncedSearchText] = useState(
    () => searchText ?? "",
  );
  const [medicalEmployee, setMedicalEmployee] = useState<Employee | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editEmployee, setEditEmployee] = useState<Employee | null>(null);
  const [openVisa, setOpenVisa] = useState(false);
  const [agreementEmployee, setAgreementEmployee] = useState<Employee | null>(
    null,
  );

  const canManage = isPrivilegedTransaction(
    user?.roles ?? [],
    RoleTypes.ManageCalling,
  );

  useEffect(() => {
    const id = window.setTimeout(
      () => setDebouncedSearchText(searchText ?? ""),
      400,
    );
    return () => window.clearTimeout(id);
  }, [searchText]);

  const listFetchParams = useMemo(
    (): EmployeeArgs => ({
      skip: currentPage * rowsPerPage,
      take: rowsPerPage,
      searchText: debouncedSearchText || undefined,
      calling: true,
      agencyId,
    }),
    [currentPage, rowsPerPage, debouncedSearchText, agencyId],
  );

  const { refreshList } = useEmployeeListFetch(listFetchParams);

  const openMedicalDialog = (employee: Employee) => {
    dispatch(setSelectedEmployee(employee));
    setMedicalEmployee(employee);
  };

  const handleMedicalClose = (saved?: boolean) => {
    setMedicalEmployee(null);
    if (saved) refreshList();
  };

  const openNewForm = () => {
    setEditEmployee(null);
    setFormOpen(true);
  };

  const openEditForm = async (employee: Employee) => {
    if (!employee._id) return;
    dispatch(resetSuccess());
    dispatch(resetError());
    const result = await dispatch(getEmployee(String(employee._id)));
    const loaded = getEmployee.fulfilled.match(result)
      ? result.payload
      : employee;
    setEditEmployee(loaded);
    setFormOpen(true);
  };

  const handleFormClose = (saved?: boolean) => {
    setFormOpen(false);
    setEditEmployee(null);
    if (saved) refreshList();
  };

  const openAgreementDialog = async (employee: Employee) => {
    if (!employee._id) return;
    dispatch(resetSuccess());
    dispatch(resetError());
    const result = await dispatch(getEmployee(String(employee._id)));
    const loaded = getEmployee.fulfilled.match(result)
      ? result.payload
      : employee;
    dispatch(setSelectedEmployee(loaded));
    setAgreementEmployee(loaded);
    setOpenVisa(true);
  };

  const dialogCloseVisa = useCallback(() => {
    dispatch(resetSuccess());
    dispatch(resetError());
    setOpenVisa(false);
    const empId = agreementEmployee?._id;
    setAgreementEmployee(null);
    const updated = store.getState().processes.selectedEmployee as
      | Employee
      | undefined;
    refreshList();
    if (
      empId &&
      (updated?.status === EmployeeStatus.contracted ||
        updated?.calling === false)
    ) {
      dispatch(removeEmployeeFromListById(String(empId)));
    }
  }, [agreementEmployee, dispatch, refreshList]);

  const handleRefresh = () => {
    void refreshOfficeContext();
    refreshList();
  };

  const showSkeleton = listLoading === "pending" && employees.length === 0;
  const colSpan = canManage ? 8 : 7;

  return (
    <>
      <Helmet>
        <title>{t("calling.title")} | CvPlus</title>
      </Helmet>

      <Box sx={{ m: { xs: 0, md: 1 }, px: { xs: 0, md: 2 } }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ mb: 1, flexWrap: "wrap", gap: 1 }}
        >
          <Box>
            <Typography variant="h5" fontWeight={800} color="primary">
              {t("calling.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("calling.subtitle")}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title={t("refresh")}>
              <span>
                <Button
                  color="secondary"
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={handleRefresh}
                  disabled={listLoading === "pending" || officeContextLoading}
                >
                  {t("refresh")}
                </Button>
              </span>
            </Tooltip>
            {canManage && (
              <Button
                color="secondary"
                variant="contained"
                startIcon={<Add />}
                onClick={openNewForm}
              >
                {t("calling.addNew")}
              </Button>
            )}
          </Stack>
        </Stack>

        <Box sx={{ maxWidth: 560, mb: 1 }}>
          <Search initialValue={searchText} />
        </Box>

        {officeContextError && (
          <Toast severity="error">{officeContextError}</Toast>
        )}

        <TableContainer component={Paper} sx={{ position: "relative" }}>
          {listLoading === "pending" && (
            <LinearProgress
              color="secondary"
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                zIndex: 2,
              }}
            />
          )}
          <Table size="medium" stickyHeader>
            <TableHead>
              <StyledTableRow>
                <StyledTableCell width="3%">{t("sno")}</StyledTableCell>
                <StyledTableCell width="6%">{t("photo")}</StyledTableCell>
                <StyledTableCell>{t("name")}</StyledTableCell>
                <StyledTableCell>{t("calling.office")}</StyledTableCell>
                <StyledTableCell>{t("epro")}</StyledTableCell>
                <StyledTableCell>{te("medical")}</StyledTableCell>
                <StyledTableCell>{t("status")}</StyledTableCell>
                {canManage && <StyledTableCell>{t("actions")}</StyledTableCell>}
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {showSkeleton ? (
                <StyledTableRow>
                  <StyledTableCell
                    colSpan={colSpan}
                    align="center"
                    sx={{ py: 6 }}
                  >
                    <LinearProgress
                      color="secondary"
                      sx={{ maxWidth: 240, mx: "auto" }}
                    />
                  </StyledTableCell>
                </StyledTableRow>
              ) : employees.length === 0 ? (
                <StyledTableRow>
                  <StyledTableCell
                    colSpan={colSpan}
                    align="center"
                    sx={{ py: 6 }}
                  >
                    <Stack
                      alignItems="center"
                      spacing={1.5}
                      sx={{ opacity: 0.55 }}
                    >
                      <PeopleOutline
                        sx={{ fontSize: 52, color: "text.disabled" }}
                      />
                      <Typography variant="body1" color="text.secondary">
                        {t("calling.empty")}
                      </Typography>
                    </Stack>
                  </StyledTableCell>
                </StyledTableRow>
              ) : (
                employees.map((row, index) => (
                  <StyledTableRow key={row._id}>
                    <StyledTableCell>
                      <Typography color="primary" variant="h6">
                        {(
                          currentPage * rowsPerPage +
                          index +
                          1
                        ).toLocaleString()}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Avatar
                        component={RouterLink}
                        alt="avatar"
                        src={row.facePhoto}
                        sx={{ cursor: "pointer", height: 40, width: 40 }}
                        to={`/app/employee-detail/${row._id}`}
                      />
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{ color: "primary.main", fontWeight: 700 }}
                    >
                      <Stack spacing={0.25}>
                        <Button
                          component={RouterLink}
                          to={`/app/employee-detail/${row._id}`}
                          size="small"
                          sx={{
                            p: 0,
                            minWidth: 0,
                            fontWeight: 700,
                            justifyContent: "flex-start",
                          }}
                        >
                          {row.givenName} {row.surName}
                        </Button>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary", fontSize: "0.8rem" }}
                        >
                          {row.passportNumber}
                        </Typography>
                      </Stack>
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{ color: "secondary.main", fontWeight: 700 }}
                    >
                      <Stack spacing={0.25}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "secondary.main" }}
                        >
                          {officeDisplayLabel(
                            employeeOfficeForDisplay(row, partnerSource),
                            theme.direction,
                          ) ??
                            (row.additionalData?.appliedCountry
                              ? te(row.additionalData.appliedCountry)
                              : "—")}
                        </Typography>
                        {row.additionalData?.appliedCountry &&
                          employeeOfficeForDisplay(row, partnerSource) && (
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary" }}
                            >
                              {te(row.additionalData.appliedCountry)}
                            </Typography>
                          )}
                      </Stack>
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{ color: "secondary.main", fontWeight: 700 }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: "secondary.main" }}
                      >
                        {officeDisplayLabel(
                          employeeOwnerForDisplay(row, partnerSource),
                          theme.direction,
                        ) ?? "—"}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <MedicalStatusChip status={row.medicalStatus} />
                    </StyledTableCell>
                    <StyledTableCell>
                      <EmployeeStatusChip
                        status={row.status as EmployeeStatus}
                      />
                    </StyledTableCell>
                    {canManage && (
                      <StyledTableCell>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title={t("edit")}>
                            <Button
                              size="small"
                              color="secondary"
                              variant="outlined"
                              startIcon={<Edit />}
                              onClick={() => openEditForm(row)}
                            >
                              {t("edit")}
                            </Button>
                          </Tooltip>
                          <Tooltip title={te("medical")}>
                            <Button
                              size="small"
                              color="secondary"
                              variant="outlined"
                              startIcon={<LocalHospital />}
                              onClick={() => openMedicalDialog(row)}
                            >
                              {te("medical")}
                            </Button>
                          </Tooltip>
                          <Tooltip
                            title={
                              row.medicalStatus === MedicalStatus.fit
                                ? t("addAgreement")
                                : te("fit")
                            }
                          >
                            <span>
                              <Button
                                size="small"
                                color="secondary"
                                variant="contained"
                                startIcon={<Assignment />}
                                disabled={
                                  row.medicalStatus !== MedicalStatus.fit
                                }
                                onClick={() => void openAgreementDialog(row)}
                              >
                                {t("calling.addContract")}
                              </Button>
                            </span>
                          </Tooltip>
                        </Stack>
                      </StyledTableCell>
                    )}
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {employees.length > 0 && (
          <Stack spacing={1} alignItems="center" sx={{ mt: 2 }}>
            <Typography
              variant="h6"
              color="white"
              sx={{
                backgroundColor: "primary.main",
                p: 1,
                borderRadius: "4px",
              }}
            >
              {t("total")}: {totalCount}
            </Typography>
            <Paging
              total={totalCount}
              rowsPerPage={rowsPerPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              setRowsPerPage={setRowsPerPage}
            />
          </Stack>
        )}
      </Box>

      {medicalEmployee && (
        <CustomDialog
          title={`${te("medical")} — ${medicalEmployee.givenName} ${medicalEmployee.surName}`}
          isOpen={Boolean(medicalEmployee)}
          handleDialogClose={() => handleMedicalClose(false)}
          maxWidth="sm"
          fullWidth
        >
          <MedicalDialog
            currentEmployee={medicalEmployee}
            handleDialogClose={handleMedicalClose}
          />
        </CustomDialog>
      )}

      {formOpen && (
        <CustomDialog
          title={editEmployee ? t("edit") : t("calling.addNew")}
          isOpen={formOpen}
          handleDialogClose={() => handleFormClose(false)}
          maxWidth="xs"
          fullWidth
          paperSx={{ maxWidth: 480 }}
        >
          <CallingForm
            currentEmployee={editEmployee}
            handleDialogClose={handleFormClose}
            officeContext={officeContext}
            officeContextLoading={officeContextLoading}
          />
        </CustomDialog>
      )}

      {openVisa && (
        <CustomDialog
          title={t("addAgreement")}
          isOpen={openVisa}
          handleDialogClose={dialogCloseVisa}
        >
          <VisaEntry
            currentEmployee={agreementEmployee ?? undefined}
            handleDialogClose={dialogCloseVisa}
            officeContext={officeContext}
          />
        </CustomDialog>
      )}
    </>
  );
};
